import {isDebugEnabled} from '../helpers/debugHelper';

// cf https://www.meticulous.ai/blog/getting-started-with-react-logging
export interface LogFn {
  (message?: any, ...optionalParams: any[]): void;
}

export interface Logger {
  error: LogFn;
  info: LogFn;
  debug: LogFn;
  trace: LogFn;
}

const DEBUG_LEVELS = {
  ERROR : 0,
  INFO : 1,
  DEBUG : 2,
  TRACE : 3,
};
const DEBUG_LEVELS_NAMES = ['ERROR','INFO','DEBUG','TRACE'];

/**
 * Service gérant les logs
 * @folder Services
 * @class
 *
 * Gère :
 * * l'activation ou non des log console selon les variables d'environnements
 * * la simplification de l'insertion des logs dans le code
 * * le formattage du log selon le niveau utilisé
 *
 * Usage :
 * ```
 * LoggerService.getInstance().init();
 * LoggerService.getInstance().debug();
 * LoggerService.getInstance().error(' Test error',{test:'test'});
 * LoggerService.getInstance().info('Test info',{test:'test'});
 * LoggerService.getInstance().debug('Test debug',{test:'test'});
 * LoggerService.getInstance().trace('Test trace',{test:'test'});
 * ```
 *
 */
class LoggerService implements Logger {
  private static _instance: LoggerService;

  debugEnabled?: boolean;  // true si debug activé
  level = 0; // 0 error 1 info 2 debug 3 trace

  error: LogFn;
  info: LogFn;
  debug: LogFn;
  trace: LogFn;

  /**
   * Renvoie l'instance du service
   * @returns {LoggerService} singleton instance
   */
  static getInstance() {
    if (!LoggerService._instance) {
      LoggerService._instance = new LoggerService();
    }
    return LoggerService._instance;
  }

  private constructor() {

    this.error = () => {};
    this.info = () => {};
    this.debug = () => {};
    this.trace = () => {};

    this.init();
  }

  /**
   * Initialise
   * @param debug
   */
  init() {
    this.debugEnabled = isDebugEnabled();
    const envLevel = process.env.REACT_APP_DEBUG_LEVEL;
    if (envLevel !== undefined) {
      switch (envLevel){
        case 'TRACE':
        case 'DEBUG':
        case 'INFO':
          this.level = DEBUG_LEVELS[envLevel];
          break;
        default:
          this.level = DEBUG_LEVELS.ERROR;
          break;
      }
    }
    const levelIndicator = `${this.level} - ${DEBUG_LEVELS_NAMES[this.level]}`;


    this.error = this.debugEnabled && this.level >= DEBUG_LEVELS.ERROR ? console.error.bind(console) : () => {};
    this.info = this.debugEnabled && this.level >= DEBUG_LEVELS.INFO ? console.info.bind(console) : () => {};
    this.debug = this.debugEnabled && this.level >= DEBUG_LEVELS.DEBUG ? console.log.bind(console) : () => {};
    this.trace = this.debugEnabled && this.level >= DEBUG_LEVELS.TRACE ? console.log.bind(console) : () => {};

    this.info('LoggerService initialized with',{debugEnabled:this.debugEnabled,level:levelIndicator,appVersion:process.env.REACT_APP_VERSION,appEnv:process.env.REACT_APP_ENV});
    if (this.debugEnabled) {
      this.error('Test LOG : ERROR level');
      this.info(...formatInfo('Test LOG : INFO level'));
      this.debug('Test LOG : DEBUG level');
      this.trace(...formatTrace('Test LOG : TRACE level'));
    }

  }

}
export default LoggerService;

/**
 * Log si enabled
 * @param arg1 texte en blue
 * @param arg autres arguments
 * @returns
 */
const formatInfo = (arg1: string,...arg: any[]):any[] => {
  return ['%c'+arg1,"color:#29b6f6",...arg];
}

/**
 * Log en gris clair le premier texte. Peut être suivi d'autres arguments.
 * @param arg1 texte en gris clair
 * @param arg autres arguments
 * @returns
 */
const formatTrace = (arg1: string,...arg: any[]):any[] => {
  return ['%c'+arg1,"color:lightgrey",...arg];
}



export {formatInfo,formatTrace};
